import { useMemo } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18N_ROUTING_ENABLED, type AppLocale } from '../i18n';
import { localePrefix, localizedPath, routeIdFromSlug, translatePath } from '../i18n/slugMap';

/**
 * Wrapper de `<Link>` que respeita o idioma corrente quando a flag
 * `I18N_ROUTING_ENABLED` está ON. Quando OFF (PROD legado), comportamento
 * é idêntico ao `<Link>` cru.
 *
 * Regras de transformação do `to`:
 * - Não-string (objeto Location) ou flag OFF → passa direto.
 * - Já tem prefix `/en/` ou `/es/` → preserva (caller foi explícito).
 * - Anchor puro `#xx` → preserva (mesma página).
 * - Locale corrente é pt-BR → preserva (não há prefix em PT-BR).
 * - `/slug` cujo `slug` resolve um routeId conhecido → reescreve pro slug do
 *   idioma corrente via `localizedPath(routeId, locale)`. Ex.: `/bolao-brasileirao`
 *   em EN vira `/en/brazilian-league-pool`.
 * - Outros paths (rotas dinâmicas, anchors com path tipo `/#features`) →
 *   apenas prepend do `localePrefix`. Ex.: `/#faq` em EN vira `/en#faq`.
 *
 * Isso resolve o bug observado em DEV: ao clicar num menu/anchor ou card
 * de competição, o user voltava pra raiz PT mesmo após trocar pra EN.
 */
export function LocalizedLink({ to, ...rest }: LinkProps) {
  const { i18n } = useTranslation();
  const locale = (i18n.language || 'pt-BR') as AppLocale;

  const target = useMemo<LinkProps['to']>(() => {
    if (typeof to !== 'string' || !I18N_ROUTING_ENABLED) return to;
    if (locale === 'pt-BR') return to;
    if (to.startsWith('#')) return to;
    if (/^\/(en|es)(\/|$|#|\?)/.test(to)) return to;

    // Separa "/slug" da query/hash pra mexer só no slug.
    const m = to.match(/^\/([^/?#]*)([/?#].*)?$/);
    if (m) {
      const slug = m[1];
      const tail = m[2] ?? '';

      // Home raiz '/' (slug vazio) → '/en' ou '/es' (mantém query/hash).
      if (slug === '') {
        const prefix = localePrefix(locale);
        return `${prefix || '/'}${tail}`;
      }

      // Slug PT conhecido, sem subpath → reescreve pro slug do idioma.
      // (caminho rápido — evita custo do translatePath quando não há /)
      if (!tail.startsWith('/')) {
        const routeId = routeIdFromSlug(slug, 'pt-BR');
        if (routeId) {
          return `${localizedPath(routeId, locale)}${tail}`;
        }
      }
    }

    // Paths multi-segmento (ex: /pools/123/matches, /admin/notificacoes,
    // /competicoes/4/partidas) — tenta tradução segmento-a-segmento que
    // preserva IDs dinâmicos no meio. Quando não casa rota conhecida,
    // fallback de prepend simples do prefix de locale.
    const translated = translatePath(to, locale);
    if (translated) return translated;
    return `/${locale}${to}`;
  }, [to, locale]);

  return <Link to={target} {...rest} />;
}

export default LocalizedLink;
